import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { apiFetch, ApiError } from "../../lib/api";
import { youtubeWatchUrl } from "../../lib/youtube";
import { ADMIN_DASHBOARD_PATH } from "../../lib/adminPaths";

/** @typedef {{
 *   id: number,
 *   youtubeVideoId: string,
 *   title: string,
 *   description?: string,
 *   thumbnailUrl?: string | null,
 *   channelTitle?: string,
 *   durationLabel?: string,
 *   publishedLabel?: string,
 *   category?: string,
 *   featuredSlot?: number | null,
 * }} AdminVideo */

function VideoRow({
  video,
  index,
  total,
  busy,
  onMove,
  onDelete,
  onUpdate,
}) {
  const [category, setCategory] = useState(video.category || "");
  const [savingCategory, setSavingCategory] = useState(false);
  const [savingFeatured, setSavingFeatured] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setCategory(video.category || "");
  }, [video.category]);

  async function handleFeaturedChange(event) {
    const value = event.target.value;
    const featuredSlot = value === "" ? null : Number(value);

    setSavingFeatured(true);
    setError("");

    try {
      const body = await apiFetch(`/admin/youtube-videos/${video.id}`, {
        method: "PATCH",
        body: JSON.stringify({ featuredSlot }),
      });
      onUpdate(body.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't update featured slot.");
    } finally {
      setSavingFeatured(false);
    }
  }

  async function handleCategorySave(event) {
    event.preventDefault();
    setSavingCategory(true);
    setError("");

    try {
      const body = await apiFetch(`/admin/youtube-videos/${video.id}`, {
        method: "PATCH",
        body: JSON.stringify({ category: category.trim() }),
      });
      onUpdate(body.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save category.");
    } finally {
      setSavingCategory(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    setError("");

    try {
      const body = await apiFetch(`/admin/youtube-videos/${video.id}/refresh-metadata`, {
        method: "POST",
      });
      onUpdate(body.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't refresh from YouTube.");
    } finally {
      setRefreshing(false);
    }
  }

  const rowBusy = busy || savingCategory || savingFeatured || refreshing;

  return (
    <li className="admin-photo-item admin-video-item">
      <div className="admin-photo-item__order">
        <span className="admin-photo-item__position">{index + 1}</span>
        <div className="admin-photo-item__order-buttons">
          <button
            className="admin-order-button"
            type="button"
            aria-label={`Move video ${index + 1} up`}
            disabled={index === 0 || rowBusy}
            onClick={() => onMove(index, -1)}
          >
            ↑
          </button>
          <button
            className="admin-order-button"
            type="button"
            aria-label={`Move video ${index + 1} down`}
            disabled={index === total - 1 || rowBusy}
            onClick={() => onMove(index, 1)}
          >
            ↓
          </button>
        </div>
      </div>

      <img
        className="admin-photo-item__thumb"
        src={video.thumbnailUrl || ""}
        alt={video.title || "YouTube thumbnail"}
      />

      <div className="admin-photo-item__body">
        <h3 className="admin-photo-item__title">{video.title || "Untitled"}</h3>
        <p className="admin-muted admin-video-item__meta">
          {video.channelTitle || "YouTube"}
          {video.publishedLabel ? ` · ${video.publishedLabel}` : ""}
          {video.durationLabel ? ` · ${video.durationLabel}` : ""}
        </p>
        {video.description ? (
          <p className="admin-photo-item__description admin-video-item__description">{video.description}</p>
        ) : (
          <p className="admin-muted">No description loaded yet.</p>
        )}

        <div className="admin-video-item__fields">
          <label className="admin-field admin-video-item__field">
            <span className="admin-label">Homepage feature</span>
            <select
              className="admin-input"
              value={video.featuredSlot ?? ""}
              disabled={rowBusy}
              onChange={(event) => void handleFeaturedChange(event)}
            >
              <option value="">Not featured</option>
              <option value="1">Featured slot 1</option>
              <option value="2">Featured slot 2</option>
            </select>
          </label>

          <form className="admin-video-item__category" onSubmit={(event) => void handleCategorySave(event)}>
            <label className="admin-field">
              <span className="admin-label">
                Category label <span className="admin-label-optional">(optional)</span>
              </span>
              <input
                className="admin-input"
                type="text"
                value={category}
                disabled={rowBusy}
                placeholder="Documentary, Motorsport…"
                onChange={(event) => setCategory(event.target.value)}
              />
            </label>
            <button className="admin-button admin-button--ghost" type="submit" disabled={rowBusy}>
              {savingCategory ? "Saving…" : "Save label"}
            </button>
          </form>
        </div>

        {error ? <p className="admin-error">{error}</p> : null}
      </div>

      <div className="admin-photo-item__actions">
        <a
          className="admin-button admin-button--ghost"
          href={youtubeWatchUrl(video.youtubeVideoId)}
          target="_blank"
          rel="noreferrer"
        >
          Open on YouTube
        </a>
        <button
          className="admin-button admin-button--ghost"
          type="button"
          disabled={rowBusy}
          onClick={() => void handleRefresh()}
        >
          {refreshing ? "Refreshing…" : "Refresh from YouTube"}
        </button>
        <button
          className="admin-button admin-button--ghost admin-button--danger"
          type="button"
          disabled={rowBusy}
          onClick={() => onDelete(video.id)}
        >
          Delete
        </button>
      </div>
    </li>
  );
}

export default function AdminVideosPage() {
  const { logout } = useAdminAuth();
  const [videos, setVideos] = useState(/** @type {AdminVideo[]} */ ([]));
  const [loading, setLoading] = useState(true);
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [reorderingId, setReorderingId] = useState(null);
  const [error, setError] = useState("");

  const loadVideos = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const body = await apiFetch("/admin/youtube-videos");
      setVideos(body.data.videos || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load videos — try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    void loadVideos();
  }, [loadVideos]);

  async function handleAdd(event) {
    event.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) {
      setError("Paste a YouTube link first.");
      return;
    }

    setAdding(true);
    setError("");

    try {
      await apiFetch("/admin/youtube-videos", {
        method: "POST",
        body: JSON.stringify({ url: trimmed }),
      });
      setUrl("");
      await loadVideos();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't add that video — try again.");
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Remove this video from your site?")) {
      return;
    }

    setDeletingId(id);
    setError("");

    try {
      await apiFetch(`/admin/youtube-videos/${id}`, { method: "DELETE" });
      await loadVideos();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Delete failed — try again.");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleMove(index, direction) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= videos.length) {
      return;
    }

    const nextVideos = [...videos];
    [nextVideos[index], nextVideos[targetIndex]] = [nextVideos[targetIndex], nextVideos[index]];

    const movedId = videos[index].id;
    const previousVideos = videos;

    setReorderingId(movedId);
    setVideos(nextVideos);
    setError("");

    try {
      const body = await apiFetch("/admin/youtube-videos/reorder", {
        method: "PATCH",
        body: JSON.stringify({
          order: nextVideos.map((video) => video.id),
        }),
      });
      setVideos(body.data.videos || nextVideos);
    } catch (err) {
      setVideos(previousVideos);
      setError(err instanceof ApiError ? err.message : "Couldn't reorder videos — try again.");
    } finally {
      setReorderingId(null);
    }
  }

  function handleVideoUpdate(updated) {
    setVideos((current) => current.map((video) => (video.id === updated.id ? updated : video)));
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div>
          <Link className="admin-back-link" to={ADMIN_DASHBOARD_PATH}>
            ← Dashboard
          </Link>
          <p className="admin-eyebrow">VIDEO</p>
          <h1 className="admin-title admin-title--compact">YouTube Videos</h1>
        </div>
        <button className="admin-button admin-button--ghost" type="button" onClick={() => void logout()}>
          Sign out
        </button>
      </header>

      <section className="admin-card admin-card--compact-upload">
        <form className="admin-upload-row" onSubmit={(event) => void handleAdd(event)}>
          <div className="admin-upload-row__field">
            <label className="admin-label" htmlFor="youtube-url">
              Add from YouTube
            </label>
            <input
              className="admin-input"
              id="youtube-url"
              name="url"
              type="url"
              placeholder="https://www.youtube.com/watch?v=…"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
            />
            <p className="admin-hint">
              Paste any public YouTube link — title, channel, and thumbnail are fetched automatically.
            </p>
          </div>
          <button className="admin-button admin-upload-row__button" type="submit" disabled={adding || !url.trim()}>
            {adding ? "Adding…" : "Add video"}
          </button>
        </form>
        {error ? <p className="admin-error">{error}</p> : null}
      </section>

      <section className="admin-card" style={{ marginTop: 20 }}>
        <div className="admin-section-header">
          <div>
            <p className="admin-eyebrow">Library</p>
            <h2 className="admin-tool-card__title">Your videos</h2>
          </div>
          <span className="admin-muted">{videos.length} total</span>
        </div>

        <p className="admin-muted admin-muted--spaced">
          Choose up to two videos for the home page Featured Video section. Order here controls the Videos page.
        </p>

        {loading ? <p className="admin-muted">Loading videos…</p> : null}

        {!loading && videos.length === 0 ? (
          <p className="admin-muted">No videos yet — paste a YouTube link above to get started.</p>
        ) : null}

        <ul className="admin-photo-list">
          {videos.map((video, index) => (
            <VideoRow
              key={video.id}
              video={video}
              index={index}
              total={videos.length}
              busy={deletingId === video.id || reorderingId === video.id}
              onMove={handleMove}
              onDelete={handleDelete}
              onUpdate={handleVideoUpdate}
            />
          ))}
        </ul>
      </section>
    </div>
  );
}
